import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { 
  Dialog, 
  DialogContent, 
  DialogDescription, 
  DialogFooter, 
  DialogHeader, 
  DialogTitle, 
  DialogTrigger 
} from "@/components/ui/dialog";
import { 
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { 
  Plus, 
  Edit, 
  Trash2, 
  Building2, 
  Users,
  HelpCircle
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";

interface Department {
  id: string;
  name: string;
  description: string | null;
  color: string;
  created_at: string;
  updated_at: string;
  policy_count?: number;
  member_count?: number;
}

const DepartmentManagement = () => {
  const { toast } = useToast();
  const { profile, hasRole } = useAuth();
  const [departments, setDepartments] = useState<Department[]>([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingDepartment, setEditingDepartment] = useState<Department | null>(null);
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    color: "#6366f1"
  });

  const isAdmin = hasRole ? hasRole('admin') : false;

  // Load departments
  const loadDepartments = async () => {
    if (!profile?.company_id) return;
    
    setLoading(true);
    try {
      // Get departments with policy counts and member counts
      const { data: departmentsData, error: departmentsError } = await supabase
        .from('departments')
        .select('*')
        .eq('company_id', profile.company_id)
        .order('name');

      if (departmentsError) throw departmentsError;

      // Get policy counts for all departments
      const { data: policiesData, error: policiesError } = await supabase
        .from('policies')
        .select('department')
        .eq('company_id', profile.company_id);

      if (policiesError) throw policiesError;

      // Get member counts for all departments
      const { data: membersData, error: membersError } = await supabase
        .from('profiles')
        .select('department_id')
        .eq('company_id', profile.company_id);

      if (membersError) throw membersError;

      // Count policies per department
      const policyCounts = (policiesData || []).reduce((acc, policy) => {
        const deptName = policy.department;
        if (deptName) {
          const normalizedName = deptName.toLowerCase().trim();
          acc[normalizedName] = (acc[normalizedName] || 0) + 1;
        }
        return acc;
      }, {} as Record<string, number>);

      // Count members per department
      const memberCounts = (membersData || []).reduce((acc, member) => {
        if (member.department_id) {
          acc[member.department_id] = (acc[member.department_id] || 0) + 1;
        }
        return acc;
      }, {} as Record<string, number>);

      // Combine departments with their counts
      const departmentsWithCounts = (departmentsData || []).map(dept => ({
        ...dept,
        policy_count: policyCounts[dept.name.toLowerCase().trim()] || 0,
        member_count: memberCounts[dept.id] || 0
      }));

      setDepartments(departmentsWithCounts);
    } catch (error: any) {
      toast({
        title: "Error",
        description: "Failed to load departments: " + (error.message || "Unknown error"),
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDepartments();
  }, [profile?.company_id]);

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!isAdmin) {
      toast({
        title: "Permission Denied",
        description: "Only administrators can manage departments.",
        variant: "destructive"
      });
      return;
    }
    
    if (!profile?.company_id) {
      toast({
        title: "Error",
        description: "No company associated with your profile.",
        variant: "destructive"
      });
      return;
    }

    if (!formData.name.trim()) {
      toast({
        title: "Error",
        description: "Department name is required.",
        variant: "destructive"
      });
      return;
    }

    try {
      if (editingDepartment) {
        // Update existing department
        const { error } = await supabase
          .from('departments')
          .update({
            name: formData.name.trim(),
            description: formData.description.trim() || null,
            color: formData.color,
            updated_at: new Date().toISOString()
          })
          .eq('id', editingDepartment.id);

        if (error) throw error;

        toast({
          title: "Success",
          description: "Department updated successfully."
        });
      } else {
        // Create new department
        const { error } = await supabase
          .from('departments')
          .insert({
            name: formData.name.trim(),
            description: formData.description.trim() || null,
            color: formData.color,
            company_id: profile.company_id,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          });

        if (error) throw error;

        toast({
          title: "Success",
          description: "Department created successfully."
        });
      }

      // Reset form and close dialog
      setFormData({ name: "", description: "", color: "#6366f1" });
      setEditingDepartment(null);
      setIsDialogOpen(false);
      loadDepartments();
    } catch (error: any) {
      toast({
        title: "Error",
        description: "Failed to save department: " + (error.message || "Unknown error"),
        variant: "destructive"
      });
    }
  };

  // Handle edit
  const handleEdit = (department: Department) => {
    setEditingDepartment(department);
    setFormData({
      name: department.name,
      description: department.description || "",
      color: department.color
    });
    setIsDialogOpen(true);
  };

  // Handle delete
  const handleDelete = async (departmentId: string) => {
    if (!isAdmin) {
      toast({
        title: "Permission Denied",
        description: "Only administrators can delete departments.",
        variant: "destructive"
      });
      return;
    }

    try {
      const { error } = await supabase
        .from('departments')
        .delete()
        .eq('id', departmentId);

      if (error) throw error;

      toast({
        title: "Success",
        description: "Department deleted successfully."
      });

      loadDepartments();
    } catch (error: any) {
      toast({
        title: "Error",
        description: "Failed to delete department: " + (error.message || "Unknown error"),
        variant: "destructive"
      });
    }
  };

  // Reset form
  const resetForm = () => {
    setFormData({ name: "", description: "", color: "#6366f1" });
    setEditingDepartment(null);
  };

  if (loading) {
    return (
      <Card className="shadow-card">
        <CardContent className="p-6">
          <div className="flex items-center justify-center h-32">
            <div className="text-muted-foreground">Loading departments...</div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!isAdmin) {
    return (
      <Card className="shadow-card">
        <CardContent className="p-6">
          <div className="text-center py-8">
            <Building2 className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-medium text-foreground mb-2">Admin Access Required</h3>
            <p className="text-muted-foreground">
              Only administrators can manage departments. Contact your administrator to create or modify departments.
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <Card className="shadow-card">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Building2 className="w-5 h-5" />
                Department Management
              </CardTitle>
              <p className="text-sm text-muted-foreground mt-1">
                Create and manage organizational departments for policy categorization and team organization.
              </p>
            </div>
            <Dialog open={isDialogOpen} onOpenChange={(open) => {
              setIsDialogOpen(open);
              if (!open) resetForm();
            }}>
              <DialogTrigger asChild>
                <Button disabled={!isAdmin}>
                  <Plus className="w-4 h-4 mr-2" />
                  Add Department
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-[425px]">
                <DialogHeader>
                  <DialogTitle>
                    {editingDepartment ? "Edit Department" : "Add New Department"}
                  </DialogTitle>
                  <DialogDescription>
                    {editingDepartment 
                      ? "Update the department information below."
                      : "Create a new department for your organization."
                    }
                  </DialogDescription>
                </DialogHeader>
                <form onSubmit={handleSubmit}>
                  <div className="space-y-4 py-4">
                    <div className="space-y-2">
                      <Label htmlFor="name">Department Name *</Label>
                      <Input
                        id="name"
                        value={formData.name}
                        onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                        placeholder="e.g., Human Resources, Finance, IT"
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="description">Description</Label>
                      <Textarea
                        id="description"
                        value={formData.description}
                        onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                        placeholder="Brief description of the department's role..."
                        rows={3}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="color">Color</Label>
                      <div className="flex items-center gap-2">
                        <Input
                          type="color"
                          id="color"
                          value={formData.color}
                          onChange={(e) => setFormData(prev => ({ ...prev, color: e.target.value }))}
                          className="w-16 h-10 p-1"
                        />
                        <Input
                          value={formData.color}
                          onChange={(e) => setFormData(prev => ({ ...prev, color: e.target.value }))}
                          placeholder="#6366f1"
                          className="flex-1"
                        />
                      </div>
                      <p className="text-xs text-muted-foreground">
                        This color will be used for department badges and visual identification.
                      </p>
                    </div>
                  </div>
                  <DialogFooter>
                    <Button type="button" variant="outline" onClick={() => {
                      setIsDialogOpen(false);
                      resetForm();
                    }}>
                      Cancel
                    </Button>
                    <Button type="submit">
                      {editingDepartment ? "Update Department" : "Create Department"}
                    </Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </CardHeader>
        <CardContent>
          {departments.length === 0 ? (
            <div className="text-center py-8">
              <Building2 className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-medium text-foreground mb-2">No Departments Yet</h3>
              <p className="text-muted-foreground mb-4">
                Create your first department to organize policies and team members.
              </p>
              <Button onClick={() => setIsDialogOpen(true)} disabled={!isAdmin}>
                <Plus className="w-4 h-4 mr-2" />
                Create First Department
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {departments.map((department) => (
                <Card key={department.id} className="relative group hover:shadow-md transition-shadow">
                  <CardContent className="p-4">
                    <div className="space-y-3">
                      <div className="flex items-start justify-between">
                        <div className="flex items-center gap-2">
                          <div 
                            className="w-3 h-3 rounded-full flex-shrink-0"
                            style={{ backgroundColor: department.color }}
                          />
                          <h3 className="font-medium text-foreground line-clamp-1">
                            {department.name}
                          </h3>
                        </div>
                        {isAdmin && (
                          <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleEdit(department)}
                              className="h-8 w-8 p-0"
                            >
                              <Edit className="w-3 h-3" />
                            </Button>
                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-8 w-8 p-0 text-destructive hover:text-destructive"
                                >
                                  <Trash2 className="w-3 h-3" />
                                </Button>
                              </AlertDialogTrigger>
                              <AlertDialogContent>
                                <AlertDialogHeader>
                                  <AlertDialogTitle>Delete Department</AlertDialogTitle>
                                  <AlertDialogDescription>
                                    Are you sure you want to delete "{department.name}"? This action cannot be undone.
                                    {department.policy_count > 0 && (
                                      <span className="block mt-2 text-destructive">
                                        Warning: This department has {department.policy_count} policies. 
                                        These policies will need to be reassigned to other departments.
                                      </span>
                                    )}
                                    {department.member_count > 0 && (
                                      <span className="block mt-2 text-destructive">
                                        Warning: This department has {department.member_count} members. 
                                        These members will need to be reassigned to other departments.
                                      </span>
                                    )}
                                  </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                                  <AlertDialogAction
                                    onClick={() => handleDelete(department.id)}
                                    className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                  >
                                    Delete
                                  </AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          </div>
                        )}
                      </div>
                      
                      {department.description && (
                        <p className="text-sm text-muted-foreground line-clamp-2">
                          {department.description}
                        </p>
                      )}
                      
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4 text-sm text-muted-foreground">
                          <div className="flex items-center gap-1">
                            <Users className="w-3 h-3" />
                            <span>{department.member_count} members</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <Building2 className="w-3 h-3" />
                            <span>{department.policy_count} policies</span>
                          </div>
                        </div>
                        <Badge 
                          variant="secondary" 
                          className="text-xs"
                          style={{ 
                            backgroundColor: department.color + '20',
                            color: department.color,
                            borderColor: department.color + '40'
                          }}
                        >
                          {department.name}
                        </Badge>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Help Section */}
      <Card className="shadow-card">
        <CardContent className="p-6">
          <div className="flex items-start gap-3">
            <HelpCircle className="w-5 h-5 text-muted-foreground mt-0.5 flex-shrink-0" />
            <div className="space-y-2">
              <h3 className="font-medium text-foreground">About Departments</h3>
              <div className="text-sm text-muted-foreground space-y-1">
                <p>• Departments help organize policies and team members by organizational structure</p>
                <p>• Each policy and team member can be assigned to a specific department</p>
                <p>• Department colors are used for visual identification in policy cards and team views</p>
                <p>• You can filter policies and team members by department</p>
                <p>• Deleting a department will require reassigning its policies and members</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default DepartmentManagement;
